import {usePlantConfig} from '@domain/shifthandover'
import {DurationType, useEquipments, useMainEquipments} from '@domain/stoppages'
import {selectFieldFilterSx} from '@hconnect/common/styles'
import {
  DowntimeStoppageCode,
  EquipmentData,
  StoppageKind,
  allDowntimeStoppages,
  defaultDowntimeStoppages,
  shifthandoverStoppagesList
} from '@hconnect/common/types'
import {Box} from '@mui/material'
import {
  AddFilterButton,
  EventTypeFilterDropDown,
  isStoppageKind,
  StoppageCodeFilterDropDown,
  StoppageCodesMap
} from '@widgets/shift-event-filters'
import React, {useCallback, useState} from 'react'
import {useTranslation} from 'react-i18next'

import {DowntimeAnalyticsEventName, FEATURE_NAMESPACE} from '../../../consts'
import {useTrackDowntimeDashboardAnalytics} from '../../../hooks'
import {useDowntimePlantId} from '../../../providers'
import {EquipmentSearchField} from '../EquipmentFilter'
import {StoppageDurationFilter} from '../StoppageDurationFilter'

import {DateTimeRangePicker} from './DateTimeRangePicker'
import type {FilterKeys, Filters} from './filters.models'
import {filters} from './filters.models'

const STORAGE_FAILURE_KEY = 'failureKey'

type Props = {
  filteringEventName?:
    | DowntimeAnalyticsEventName.UserAdjusts2LevelFilter
    | DowntimeAnalyticsEventName.UserAdjusts3LevelFilter
    | DowntimeAnalyticsEventName.UserAdjusts4LevelFilter
  failureState: {
    failureStoppage?: StoppageKind[]
    setFailureStoppage: (failures: StoppageKind[] | undefined) => void
  }
  mainEquipmentState: {
    mainEquipments?: EquipmentData[]
    setMainEquipments: (mainEquipmentNumbers: EquipmentData[] | undefined) => void
  }
  equipmentNumbersState: {
    equipmentNumbers?: EquipmentData[]
    setEquipmentNumbers: (equipmentNumbers: EquipmentData[] | undefined) => void
  }
  stoppageCodesState: {
    stoppageCodes?: string[]
    setStoppageCodes?: (stoppageCode: string[] | undefined) => void
  }
  stoppageDurationState?: {
    stoppageDuration?: DurationType[]
    setStoppageDuration: (failures: DurationType[] | undefined) => void
  }
  options?: Filters
}

type FirstLoadFiltersData = {
  mainEquipments?: EquipmentData[]
  stoppageCodes?: string[]
  equipmentNumbers?: EquipmentData[]
  enabledFilters: Filters
  failuresOptions: {
    failureStoppage?: StoppageKind[]
    onFailureChange: (stoppage?: StoppageKind[]) => void
  }
  stoppageDuration?: DurationType[]
}
const getFirstLoadFilters = ({
  mainEquipments,
  stoppageCodes,
  equipmentNumbers,
  enabledFilters,
  failuresOptions: {failureStoppage, onFailureChange},
  stoppageDuration
}: FirstLoadFiltersData) => {
  let firstLoadFailureStoppage = failureStoppage
  const initFilters = sessionStorage.getItem(STORAGE_FAILURE_KEY)

  if (!initFilters && !firstLoadFailureStoppage) {
    firstLoadFailureStoppage = defaultDowntimeStoppages
    onFailureChange(defaultDowntimeStoppages)
  }
  sessionStorage.setItem(STORAGE_FAILURE_KEY, defaultDowntimeStoppages.join(','))

  const filters: Filters = []
  if (firstLoadFailureStoppage && enabledFilters.includes('failures')) filters.push('failures')
  if (mainEquipments && enabledFilters.includes('mainEquipmentNumbers'))
    filters.push('mainEquipmentNumbers')
  if (stoppageCodes && enabledFilters.includes('stoppageCodes')) filters.push('stoppageCodes')
  if (stoppageDuration && enabledFilters.includes('stoppageDuration'))
    filters.push('stoppageDuration')
  if (equipmentNumbers && enabledFilters.includes('equipmentNumbers'))
    filters.push('equipmentNumbers')
  return filters
}

export const DowntimeFilters: React.FC<Props> = (props) => {
  const plantId = useDowntimePlantId()
  const {filteringEventName} = props
  const {
    t,
    i18n: {language}
  } = useTranslation(FEATURE_NAMESPACE)
  const {data: config} = usePlantConfig({plantId, language})
  const trackAnalyticsEvent = useTrackDowntimeDashboardAnalytics(plantId)

  const {
    failureState: {failureStoppage, setFailureStoppage},
    mainEquipmentState: {mainEquipments, setMainEquipments},
    stoppageCodesState: {stoppageCodes, setStoppageCodes},
    equipmentNumbersState: {equipmentNumbers, setEquipmentNumbers},
    stoppageDurationState,
    options
  } = props

  const enabledFilters = options ?? filters

  const addFilterOptions = React.useMemo(
    () =>
      enabledFilters.map((filter) => ({
        value: filter,
        label: t(`filters.addFilter.${filter}`)
      })),
    [enabledFilters, t]
  )

  const [visibleFilters, setFilter] = useState<Filters>(
    getFirstLoadFilters({
      failuresOptions: {
        failureStoppage,
        onFailureChange: setFailureStoppage
      },
      mainEquipments,
      stoppageCodes,
      equipmentNumbers,
      enabledFilters,
      stoppageDuration: stoppageDurationState?.stoppageDuration
    })
  )

  const onFailureChange = useCallback(
    (failures?: StoppageKind[]) => {
      setFailureStoppage(failures)
      if (filteringEventName) {
        trackAnalyticsEvent(filteringEventName, {
          filterName: 'failures'
        })
      }
    },
    [filteringEventName, setFailureStoppage, trackAnalyticsEvent]
  )

  const onDateTimeRangeChange = useCallback(() => {
    if (filteringEventName) {
      trackAnalyticsEvent(filteringEventName, {
        filterName: 'dateRange'
      })
    }
  }, [filteringEventName, trackAnalyticsEvent])

  const onEquipmentChange = useCallback(
    (mainEquipments?: EquipmentData[]) => {
      setMainEquipments(mainEquipments)
      if (filteringEventName) {
        trackAnalyticsEvent(filteringEventName, {
          filterName: 'mainEquipments'
        })
      }
    },
    [setMainEquipments, filteringEventName, trackAnalyticsEvent]
  )

  const onStoppageCodesChange = useCallback(
    (stoppageCodes?: string[]) => {
      setStoppageCodes?.(stoppageCodes)
      if (filteringEventName) {
        trackAnalyticsEvent(filteringEventName, {
          filterName: 'stoppageCodes'
        })
      }
    },
    [setStoppageCodes, filteringEventName, trackAnalyticsEvent]
  )

  const onEquipmentNumbersChange = useCallback(
    (equipmentNumbers?: EquipmentData[]) => {
      setEquipmentNumbers(equipmentNumbers)
      if (filteringEventName) {
        trackAnalyticsEvent(filteringEventName, {
          filterName: 'equipmentNumbers'
        })
      }
    },
    [setEquipmentNumbers, filteringEventName, trackAnalyticsEvent]
  )

  const clearFilterValue = useCallback(
    (filterName: FilterKeys) => {
      switch (filterName) {
        case 'mainEquipmentNumbers':
          onEquipmentChange(undefined)
          break
        case 'failures':
          setFailureStoppage(undefined)
          break
        case 'stoppageCodes':
          onStoppageCodesChange(undefined)
          break
        case 'equipmentNumbers':
          onEquipmentNumbersChange(undefined)
          break
        case 'stoppageDuration':
          props.stoppageDurationState?.setStoppageDuration(undefined)
          break
      }
    },
    [
      onEquipmentChange,
      onEquipmentNumbersChange,
      setFailureStoppage,
      onStoppageCodesChange,
      props.stoppageDurationState
    ]
  )

  const removeFilter = useCallback(
    (filterName: FilterKeys) => {
      setFilter((prevFilters) => prevFilters.filter((pf) => pf !== filterName))
      clearFilterValue(filterName)
    },
    [clearFilterValue]
  )

  const stoppageCodesMap: StoppageCodesMap<DowntimeStoppageCode> = {
    kiln: config?.kilnStoppageCodes,
    rawMill: config?.rawMillStoppageCodes,
    cementMill: config?.finishMillStoppageCodes,
    other: [{code: 'not_available', type: StoppageKind.Other, description: 'other', priority: -1}]
  }

  if (!config) {
    return null
  }

  return (
    <Box display="flex" gap={1} alignItems="center" flexWrap="wrap">
      <DateTimeRangePicker onDateRangeChange={onDateTimeRangeChange} timezone={config.timezone} />
      {visibleFilters.includes('failures') && (
        <EventTypeFilterDropDown
          name="failures"
          data-test-id={'filter-stoppageType'}
          onChange={(value) => {
            const stoppageKindValues = value?.filter(isStoppageKind)
            onFailureChange(stoppageKindValues)
          }}
          label={t('filters.label.failureType')}
          sx={selectFieldFilterSx}
          value={failureStoppage}
          removeFilter={removeFilter}
          options={shifthandoverStoppagesList}
        />
      )}
      {visibleFilters.includes('mainEquipmentNumbers') && (
        <EquipmentSearchField
          plantId={plantId}
          value={mainEquipments}
          onChange={onEquipmentChange}
          clearFilter={() => removeFilter('mainEquipmentNumbers')}
          useApiHook={useMainEquipments}
          noEquipmentsSelectedLabelKey={'filters.label.mainEquipment'}
          selectedEquipmentsLabelKey={'filters.label.mainEquipmentNumbers'}
        />
      )}
      {visibleFilters.includes('equipmentNumbers') && (
        <EquipmentSearchField
          plantId={plantId}
          value={equipmentNumbers}
          onChange={onEquipmentNumbersChange}
          clearFilter={() => removeFilter('equipmentNumbers')}
          noEquipmentsSelectedLabelKey={'filters.label.equipment'}
          selectedEquipmentsLabelKey={'filters.label.equipment'}
          dataTestId="filter-equipment"
          useApiHook={useEquipments}
        />
      )}
      {visibleFilters.includes('stoppageCodes') && (
        <StoppageCodeFilterDropDown
          data-test-id={'events-filter-bar-stoppageCode'}
          onChange={onStoppageCodesChange}
          value={stoppageCodes}
          stoppageKinds={allDowntimeStoppages}
          stoppageCodesMap={stoppageCodesMap}
        />
      )}
      {visibleFilters.includes('stoppageDuration') && stoppageDurationState && (
        <StoppageDurationFilter
          value={stoppageDurationState.stoppageDuration}
          onChange={stoppageDurationState.setStoppageDuration}
          clearFilter={() => removeFilter('stoppageDuration')}
        />
      )}
      <Box sx={{textWrap: 'nowrap'}}>
        <AddFilterButton<FilterKeys>
          data-test-id={'stoppage-add-filter'}
          id="events-filter-bar-add-filter-button"
          options={addFilterOptions}
          buttonLabel={t('filters.label.addFilter')}
          onChange={(value: string[] = []) => {
            return setFilter((prevFilters) => {
              const intersection = prevFilters.filter((x: string) => !value.includes(x))
              const filterName = intersection[0]
              clearFilterValue(filterName)
              return value
            })
          }}
          values={visibleFilters}
        />
      </Box>
    </Box>
  )
}
